import { decodeXHRResponse } from "@/common/fetch"
import { Stream } from "@/services/common/stream"
import { Translates } from "@/services/repository/i18n"

export function selectFiles(accept?: string): Promise<FileList | null> {
    return new Promise((resolve) => {
        const input = document.createElement('input')
        input.type = 'file'
        input.accept = accept || ''
        input.multiple = true
        input.style.display = 'none'

        document.body.appendChild(input)

        input.addEventListener('change', (event) => {
            const target = event.target as HTMLInputElement
            resolve(target.files)
            document.body.removeChild(input)
        })

        input.style.display = 'none'
        input.click()
    })
}

export async function takePhoto(): Promise<FileList | null> {
    return new Promise((resolve, reject) => {
        const input = document.createElement('input')
        input.type = 'file'
        input.accept = 'image/*'
        input.capture = 'environment'
        input.style.display = 'none'

        input.addEventListener('change', (event) => {
            const target = event.target as HTMLInputElement
            const files = target.files
            if (files && files.length > 0) {
                resolve(files)
            } else {
                reject(new Error('No photo taken.'))
            }
        })

        input.click()
    })
}

export function isPhotoCaptureSupported(): boolean {
    const input = document.createElement('input')
    const isFileInputSupported = 'capture' in input

    const isMediaDevicesSupported = !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)

    // Use Media Capabilities to further enhance checks if needed
    const hasCameraCapabilities = isMediaDevicesSupported

    // If needed, add heuristic checks based on user agent here
    const isMobileDevice = /Mobi|Android/i.test(navigator.userAgent)

    return isFileInputSupported && hasCameraCapabilities && isMobileDevice
}

export interface CreateFilesResponse {
    id: string
}

export type UploadStatus = {
    started: boolean
    progress: number
    message: string | undefined
    error: boolean
}

export type uploadFilesProps = {
    url: string
    files: FileList
    statusStream: Stream<UploadStatus>
    translates: Translates
    urlToken: string | undefined
    onSuccess: (data: CreateFilesResponse | undefined) => void
    onFail?: (status: UploadStatus) => void
}

export const uploadFiles = async (props: uploadFilesProps) => {
    return new Promise((resolve, reject) => {
        doUploadFiles({
            ...props,
            onSuccess: (data) => {
                if(props.onSuccess) {
                    props.onSuccess(data)
                }
                resolve(data)
            },
            onFail: reject
        })
    })
}

export const doUploadFiles = async ({url, urlToken, translates, files, statusStream, onSuccess, onFail}: uploadFilesProps) => {
    const cleanUploadStatus = {
        started: false,
        progress: 0,
        message: undefined,
        error: false,
    }
    let lastStatus: UploadStatus

    const setUploadStatus = (status: UploadStatus) => {
        lastStatus = status
        statusStream.send(status)
        if(status.error && onFail) {
            onFail(status)
        }
    }
    
    setUploadStatus(cleanUploadStatus)
    
    const updateProgress = (progress: number) => {
        lastStatus.progress = Math.floor(progress / files.length)
        statusStream.send({...lastStatus})
    }

    if (files.length === 0) {
        setUploadStatus({
            started: true,
            progress: 0,
            message: translates.for("form.fields.upload.select_file_before_uploading"),
            error: true,
        })
        return
    }

    setUploadStatus({
        started: true,
        progress: 0,
        message: undefined,
        error: false,
    })

    try {
        let i = 1
        const formData = new FormData()
        for (const file of files) {
            formData.append("file" + i++, file)
        }

        const xhr = new XMLHttpRequest()
        xhr.open("POST", url)
        if (urlToken) {
            xhr.setRequestHeader("Authorization", `Bearer ${urlToken}`)
        }

        xhr.upload.onprogress = (event: ProgressEvent) => {
            if (event.lengthComputable) {
                const totalProgress = Math.round((event.loaded / event.total) * 100)
                updateProgress(Math.round(totalProgress))
            }
        }

        xhr.onload = () => {
            if (xhr.status === 200) {
                setUploadStatus({
                    started: false,
                    progress: 100,
                    message: translates.for("form.fields.upload.file_uploaded_successfully"),
                    error: false,
                })
                // handleClear()
                onSuccess(decodeXHRResponse<CreateFilesResponse>(xhr))
                
            } else {
                setUploadStatus({
                    started: false,
                    progress: 0,
                    message: translates.for("form.fields.upload.error_uploading_file"),
                    error: true,
                })
            }
        }

        xhr.onerror = () => {
            setUploadStatus({
                started: true,
                progress: 0,
                message: translates.for("form.fields.upload.error_uploading_file"),
                error: true,
            })
        }

        xhr.send(formData)
    } catch (error) {
        setUploadStatus({
            started: false,
            progress: 0,
            message: translates.for("form.fields.upload.unexpected_error_occurred"),
            error: true,
        })
    }
}